<template>
	<div class="progress position-absolute w-100" style="top: 0; left: 0; border-radius: 0">
		<div :style="`width: ${setupPercentCompleted}%;`" class="progress-bar bg-secondary" role="progressbar" aria-label="Setup Progress"
		     :aria-valuenow="setupPercentCompleted" aria-valuemin="0" aria-valuemax="100">
		</div>
	</div>
	<div class="row">
		<div class="w-100 w-md-50 mw-500 mx-auto pb-lg" :class="width < 500 ? 'overflow-hidden' : ''">
			<Transition :name="transitionName" mode="out-in">
				<component :is="getComponentType()" :key="currentComponentName" :width="width" :username="getUsername" @navigationButtonClicked="moveButtonAction">
				</component>
			</Transition>
		</div>
	</div>
</template>

<script setup>
import { computed, watch, onMounted, ref, onUnmounted } from "vue";
import {useAuthStore} from "@/stores/auth-store";
import {useRouter} from "vue-router";
import SetupAccount from "@/components/Auth/FirstSetup/SetupAccount.vue";
import ChooseUsername from "@/components/Auth/FirstSetup/ChooseUsername.vue";
import AuthenticatorChoose from "@/components/Auth/FirstSetup/AuthenticatorChoose.vue";
import AuthenticatorDownload from "@/components/Auth/FirstSetup/AuthenticatorDownload.vue";
import AuthenticatorSetup from "@/components/Auth/FirstSetup/AuthenticatorSetup.vue";
import AuthenticatorSetupVerify from "@/components/Auth/FirstSetup/AuthenticatorSetupVerify.vue";
import AccountSetupCompleted from "@/components/Auth/FirstSetup/AccountSetupCompleted.vue";
import { useWindowSize } from '@vueuse/core';

const { width } = useWindowSize();
const auth = useAuthStore();
const router = useRouter();

const getUsername = computed(() => {
	if(auth && auth.user && auth.user.username){
		return auth.user.username;
	}
	return 'Anonymous User';
});

const transitionName = ref('slide-continue');
const currentComponentName = ref("SetupAccount");
const setupPercentCompleted = ref(10);
const getComponentType = () => {
	const componentMapping = {
		SetupAccount: SetupAccount,
		ChooseUsername: ChooseUsername,
		AuthenticatorChoose: AuthenticatorChoose,
		AuthenticatorDownload: AuthenticatorDownload,
		AuthenticatorSetup: AuthenticatorSetup,
		AuthenticatorSetupVerify: AuthenticatorSetupVerify,
		AccountSetupCompleted: AccountSetupCompleted
	};

	return componentMapping[currentComponentName.value] || null;
};

const accountSetupComplete = computed(() => auth.user.account_setup_completed);

onMounted(() => {
	if(auth.user && accountSetupComplete.value){
		router.push({ name: 'home' });
	}
	else{
		watch(() => auth.user, () => {
			if(accountSetupComplete.value){
				router.push({ name: 'home' });
			}
		});
	}
	document.documentElement.classList.add('hide-scroll');
});

onUnmounted(() => {
	document.documentElement.classList.remove('hide-scroll');
})

const moveButtonAction = (actionName) => {
	switch(actionName){
		case 'username':
			setupPercentCompleted.value = 25;
			currentComponentName.value = 'ChooseUsername';
			break;
		case 'two-factor':
			setupPercentCompleted.value = 40;
			currentComponentName.value = 'AuthenticatorChoose';
			break;
		case 'two-factor-download':
			setupPercentCompleted.value = 50;
			currentComponentName.value = 'AuthenticatorDownload';
			break;
		case 'two-factor-setup':
			setupPercentCompleted.value = 65;
			currentComponentName.value = 'AuthenticatorSetup';
			break;
		case 'two-factor-verify':
			setupPercentCompleted.value = 80;
			transitionName.value = '';
			currentComponentName.value = 'AuthenticatorSetupVerify';
			break;
		// case 'two-factor-recovery':
		// 	setupPercentCompleted.value = 85;
		// 	currentComponentName.value = 'AuthenticatorSaveRecoveryCode';
		// 	break;
		// case 'two-factor-completed':
		// 	setupPercentCompleted.value = 90;
		// 	currentComponentName.value = 'AuthenticatorSetupCompleted';
		// 	break;
		case 'skip':
			transitionName.value = 'slide-continue';
			setupPercentCompleted.value = 100;
			currentComponentName.value = 'AccountSetupCompleted';
			break;
		case 'home':
			router.push({ name: 'home' });
			break;
		case 'profile':
			router.push({ name: 'profile' });
			break;
		default:
			break;
	}
}

</script>

<style scoped lang="scss">

.slide-continue-enter-active,
.slide-continue-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
	will-change: transform;
	transform: translateZ(0);
	transition: transform 0.45s ease-in-out;
	transform-style: preserve-3d;
}

.slide-continue-leave-to {
	transform: translateX(calc((100vw * -1) - 50px));
}

.slide-continue-enter-from {
	transform: translateX(100vw);
}

.slide-continue-enter-to {
	transform: translateX(0%);
}

.slide-prev-leave-to {
	transform: translateX(100vw);
}

.slide-prev-enter-from {
	transform: translateX(calc((100vw * -1) - 50px));
}

.slide-prev-enter-to {
	transform: translateX(0%);
}

</style>
